import moment from 'moment';
import { default as Endpoint } from '../../api/Endpoint';
import * as Constants from '../constants';

const DEMO_DATA = require('../../ct-demo.json');
const DEMO_DATA_WEEK = require('../../ct-demo-week.json');
const DEMO_DATA_MONTH = require('../../ct-demo-month.json');

export function requestExperiment(uuid, context) {
  return () => {
    return {
      type: Constants.REQUEST_EXPERIMENT,
      uuid,
      load: { context }
    };
  };
}
export function receiveExperiment(uuid, context) {
  return (json, error) => {
    return {
      type: Constants.RECEIVE_EXPERIMENT,
      data: json,
      uuid,
      load: { context },
      error
    };
  };
}

export function requestExperiments(page, load) {
  return () => {
    return {
      type: Constants.REQUEST_EXPERIMENTS,
      page,
      load
    };
  };
}
export function receiveExperiments(page, load) {
  return (json, error) => {
    return {
      type: Constants.RECEIVE_EXPERIMENTS,
      data: json,
      page,
      load,
      error
    };
  };
}

export function requestArchivedExperiments(page, load) {
  return () => {
    return {
      type: Constants.REQUEST_ARCHIVED_EXPERIMENTS,
      page,
      load
    };
  };
}
export function receiveArchivedExperiments(page, load) {
  return (json, error) => {
    return {
      type: Constants.RECEIVE_ARCHIVED_EXPERIMENTS,
      data: json,
      page,
      load,
      error
    };
  };
}

export function requestSaveExperiment(mode, context) {
  return () => {
    return {
      type: Constants.REQUEST_SAVE_EXPERIMENT,
      mode,
      context
    };
  };
}

export function receiveSaveExperiment(mode, context, experiment) {
  return (json, error) => {
    return {
      type: Constants.RECEIVE_SAVE_EXPERIMENT,
      mode,
      context,
      experiment,
      data: json,
      error
    };
  };
}

export function requestSaveSelectable(mode, context) {
  return () => {
    return {
      type: Constants.REQUEST_SAVE_SELECTABLE,
      mode,
      context
    };
  };
}

export function clearSelectableSaveContext(context = null) {
  return {
    type: Constants.CLEAR_SELECTABLE_SAVE_CONTEXT,
    context
  };
}

export function receiveSaveSelectable(mode, context, experiment, selectable) {
  return (json, error) => {
    return {
      type: Constants.RECEIVE_SAVE_SELECTABLE,
      mode,
      context,
      experiment,
      selectable,
      data: json,
      error
    };
  };
}

/**
 * filter
 *  - archived
 *    + returns assets that are either archived or not (nil = false)
 *    + values: true, or false
 *  - assetClassId
 *    + uuid of the asset class to limit results to
 *  - type
 *    + asset class name to limit the results to
 *  - name
 *    + partial string of the asset name to search
 *  - selectable
 *    + true or false to limit results to only available assets with valid date range
 *
 *  - customFilters (array of additional filters)
 *    + type (required vs. custom)
 *    + property (name of filter, special behavior for start_date and end_date)
 *    + predicate (startDate/endDate for date range, otherwise string value or '(empty)')
 *
 * pageOptions
 *  - page
 *    + page number to retrieve starting at 1
 *  - size
 *    + number of results to return (MM defaults to 25)
 *  - sortColumn
 *    - property
 *      + name of column to sort by
 *    - sortDirection
 *      + "asc" or "desc"
 *
 * loadOptions
 *  - mode
 *    + reset
 *    + combine (default)
 *  - context
 *    + name of node to put results into
 *    + paging and loading state are also grouped under this node
 *    + if left empty, results will be put under the node "default"
 *
 * @param filter
 * @param pageOptions
 * @param loadOptions
 * @param options
 * @returns {*}
 */
export function searchExperiments(
  filter = {},
  pageOptions = {},
  loadOptions = {},
  options = {}
) {
  const params = [];
  if (pageOptions.page) {
    params.push({ key: 'page', value: pageOptions.page });
  }
  if (pageOptions.size) {
    params.push({ key: 'size', value: pageOptions.size });
  }
  if (filter.name) {
    params.push({ key: 'query', value: filter.name });
  }
  if (Object.keys(filter).includes('archived')) {
    params.push({ key: 'archived', value: filter.archived });
  }
  if (filter.status) {
    params.push({ key: 'status', value: filter.status });
  }

  const sort = filter.sortColumn?.property || 'updated_at';
  const order = filter.sortColumn?.sortDirection || 'desc';
  params.push({ key: 'sort', value: sort });
  params.push({ key: 'order', value: order });

  const queryString = params.length
    ? '?' +
      params
        .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`)
        .join('&')
    : '';
  const path = `ecs/api/experiment${queryString}`;
  return new Endpoint(
    path,
    requestExperiments(pageOptions, loadOptions),
    receiveExperiments(pageOptions, loadOptions)
  )
    .withOptions(options)
    .call();
}

export function getExperiments(context, options = {}) {
  const path = 'ecs/api/experiment';

  return new Endpoint(
    path,
    requestExperiments(context),
    receiveExperiments(context)
  )
    .withOptions(options)
    .call();
}

export function getArchivedExperiments(context, options = {}) {
  const path = 'ecs/api/experiment/archived';

  return new Endpoint(
    path,
    requestArchivedExperiments(context),
    receiveArchivedExperiments(context)
  )
    .withOptions(options)
    .call();
}

export function requestExperimentPerformance(url = null) {
  return () => ({
    type: Constants.REQUEST_EXPERIMENT_PERFORMANCE,
    url
  });
}
export function receiveExperimentPerformance(url = null) {
  return (json, error) => ({
    type: Constants.RECEIVE_EXPERIMENT_PERFORMANCE,
    url,
    data: json,
    error
  });
}

export function modifyExperimentAnalyticsFilter(filter) {
  return {
    type: Constants.MODIFY_EXPERIMENT_ANALYTICS_FILTER,
    filter
  };
}

export function requestExperimentJobs() {
  return {
    type: Constants.REQUEST_EXPERIMENT_JOBS
  };
}
export function receiveExperimentJobs(json, error) {
  return {
    type: Constants.RECEIVE_EXPERIMENT_JOBS,
    data: json,
    error
  };
}

export function createExperiment(experiment, context, options = {}) {
  const path = 'ecs/api/experiment/';
  if (!experiment.engine_name) {
    experiment.engine_name = 'ABN';
  }
  if (!experiment.end_trigger) {
    experiment.end_trigger = 'MANUAL';
  }
  if (experiment.content_test_assets?.length) {
    experiment.content_test_assets_attributes = experiment.content_test_assets;
    delete experiment.content_test_assets;
  }
  if (options.status === 'PUBLISH') {
    experiment.status = 'PUBLISHED';
  }
  const payload = { content_test: experiment };

  return new Endpoint(
    path,
    requestSaveExperiment('create', context),
    receiveSaveExperiment('create', context, experiment)
  )
    .withMethod('POST')
    .withBody(JSON.stringify(payload))
    .withOptions(options)
    .call();
}

export function editExperiment(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}`;
  if (!experiment.engine_name) {
    experiment.engine_name = 'ABN';
  }
  if (!experiment.end_trigger) {
    experiment.end_trigger = 'MANUAL';
  }
  if (experiment.content_test_assets?.length) {
    experiment.content_test_assets_attributes = experiment.content_test_assets;
    delete experiment.content_test_assets;
  }
  if (options.status === 'PUBLISH') {
    experiment.status = 'PUBLISHED';
  }
  const payload = { content_test: experiment };

  return new Endpoint(
    path,
    requestSaveExperiment('edit', context),
    receiveSaveExperiment('edit', context, experiment)
  )
    .withMethod('PUT')
    .withBody(JSON.stringify(payload))
    .withOptions(options)
    .call();
}

export function getExperiment(uuid, context, options = {}) {
  const path = `ecs/api/experiment/${uuid}`;

  return new Endpoint(
    path,
    requestExperiment(uuid, context),
    receiveExperiment(uuid, context)
  )
    .withOptions(options)
    .call();
}

export function publishExperiment(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}/publish`;

  return new Endpoint(
    path,
    requestSaveExperiment('publish', context),
    receiveSaveExperiment('publish', context)
  )
    .withMethod('POST')
    .withOptions(options)
    .call();
}

export function endExperiment(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}/end`;

  return new Endpoint(
    path,
    requestSaveExperiment('end', context),
    receiveSaveExperiment('end', context)
  )
    .withMethod('POST')
    .withOptions(options)
    .call();
}

export function archiveExperiment(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}/archive`;

  return new Endpoint(
    path,
    requestSaveExperiment('archive', context),
    receiveSaveExperiment('archive', context)
  )
    .withMethod('POST')
    .withOptions(options)
    .call();
}

export function unarchiveExperiment(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}/archive`;

  return new Endpoint(
    path,
    requestSaveExperiment('unarchive', context),
    receiveSaveExperiment('unarchive', context)
  )
    .withMethod('DELETE')
    .withOptions(options)
    .call();
}

export function deleteExperiment(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}`;
  return new Endpoint(
    path,
    requestSaveExperiment('delete', context),
    receiveSaveExperiment('delete', context, experiment)
  )
    .withMethod('DELETE')
    .withOptions(options)
    .call();
}

export function createSelectable(
  experimentId,
  selectable,
  context,
  options = {}
) {
  if (!selectable.experiment_uuid) {
    delete selectable.uuid;
    delete selectable.image_url;
  }
  const path = `ecs/api/experiment/${experimentId}/selectable`;
  const payload = { content_test_asset: selectable };

  return new Endpoint(
    path,
    requestSaveSelectable('create', context),
    receiveSaveSelectable('create', context)
  )
    .withMethod('POST')
    .withBody(JSON.stringify(payload))
    .withOptions(options)
    .call();
}

export function editSelectable(experiment, selectable, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}/selectable/${selectable.uuid}`;
  const payload = { content_test_asset: selectable };

  return new Endpoint(
    path,
    requestSaveSelectable('edit', context),
    receiveSaveSelectable('edit', context)
  )
    .withMethod('PUT')
    .withBody(JSON.stringify(payload))
    .withOptions(options)
    .call();
}

export function activateSelectable(
  experiment,
  selectable,
  context,
  options = {}
) {
  const path = `ecs/api/experiment/${experiment.uuid}/selectable/${selectable.uuid}/activate`;

  return new Endpoint(
    path,
    requestSaveSelectable('activate', context),
    receiveSaveSelectable('activate', context)
  )
    .withMethod('POST')
    .withOptions(options)
    .call();
}

export function deactivateSelectable(
  experiment,
  selectable,
  context,
  options = {}
) {
  const path = `ecs/api/experiment/${experiment.uuid}/selectable/${selectable.uuid}/deactivate`;

  return new Endpoint(
    path,
    requestSaveSelectable('deactivate', context),
    receiveSaveSelectable('deactivate', context)
  )
    .withMethod('POST')
    .withOptions(options)
    .call();
}

export function deleteSelectable(
  experiment,
  selectable,
  context,
  options = {}
) {
  const path = `ecs/api/experiment/${experiment.uuid}/selectable/${selectable.uuid}`;

  return new Endpoint(
    path,
    requestSaveSelectable('delete', context),
    receiveSaveSelectable('delete', context)
  )
    .withMethod('DELETE')
    .withOptions(options)
    .call();
}

export function selectWinner(experiment, context, options = {}) {
  const path = `ecs/api/experiment/${experiment.uuid}/selectable/${experiment.selectedRow.uuid}/win`;

  return new Endpoint(
    path,
    requestSaveSelectable('winner', context),
    receiveSaveSelectable('winner', context)
  )
    .withMethod('POST')
    .withOptions(options)
    .call();
}

export function getExperimentJobs(uuid, options = {}) {
  const path = `ecs/api/experiment/${uuid}/jobs`;
  return new Endpoint(path, requestExperimentJobs, receiveExperimentJobs)
    .withOptions(options)
    .call();
}

export function experimentUrlForFilter(uuid, filter) {
  const url = `/ecs/api/experiment/${uuid}/performance`;
  if (url) {
    if (filter.startDate && filter.endDate) {
      const params = {
        v: '2',
        from: encodeURIComponent(
          moment(filter.startDate)
            .startOf('day')
            .toISOString(true)
        ),
        to: encodeURIComponent(
          moment(filter.endDate)
            .endOf('day')
            .toISOString(true)
        ),
        top: 20
      };
      if (filter.job.id !== 'none') {
        params.job_id = filter.job.id;
      }

      let qs = Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');

      return `${url}?${qs}`;
    }
  }
  return undefined;
}

export function getExperimentPerformance(experiment, filter, options = {}) {
  const path = experimentUrlForFilter(experiment.uuid, filter);
  if (path) {
    if (options.demo) {
      return dispatch => {
        dispatch(requestExperimentPerformance(path)());
        dispatch(
          receiveExperimentPerformance(path)(
            demoData(filter, options.demoPerformers)
          )
        );
      };
    }

    return new Endpoint(
      path,
      requestExperimentPerformance(path),
      receiveExperimentPerformance(path)
    )
      .withOptions(options)
      .call();
  } else {
    return dispatch => {};
  }
}

function demoData(filter, realPerformers) {
  const dateRange = filter.endDate.getTime() - filter.startDate.getTime();
  let data;
  if (dateRange > 1123200000) {
    data = DEMO_DATA_MONTH;
  } else if (dateRange > 0) {
    data = DEMO_DATA_WEEK;
  } else {
    data = DEMO_DATA;
    demoPerformers(realPerformers, data.performers);
    demoTimestamp(filter, data.details, 'hours');
    return data;
  }
  demoPerformers(realPerformers, data.performers);
  demoTimestamp(filter, data.details, 'days');
  return data;
}

function demoPerformers(realData, demoData) {
  realData.forEach((performer, idx) => {
    demoData[idx].selectable_uuid = realData[idx].selectable_uuid;
    demoData[idx].selectable.name = realData[idx].name;
    demoData[idx].selectable.image_url = realData[idx].image_url;
  });
}

function demoTimestamp(filter, demoData, scale) {
  demoData.forEach((detail, idx) => {
    demoData[idx].timestamp = moment(filter.startDate)
      .startOf('day')
      .add(idx, scale)
      .toISOString();
  });
}
